/**
 * canvasController — the single service that owns all canvas presence and
 * opacity decisions.
 *
 * ARCHITECTURE (v5.2):
 *   Two concerns, two operations:
 *     PRESENCE (in / out of canvas)  → addToCanvas, stash, restore, remove
 *     OPACITY  (dim / undim only)    → dim, undim, clearAllDims
 *
 *   `_visibility` data property is OPACITY ONLY. Writing 0.0 is deprecated —
 *   use `stash(ids, reason, by)` to actually remove an element from the
 *   canvas. Stashed elements live in `useStashStore`, tagged with the reason
 *   (user-hide / filter / algorithm / manual-stash / orphan) which controls
 *   how they get restored.
 *
 *   CONSUMERS (after full migration — these land in Phases 1–3):
 *     - Eye icon toggle    → stash([id], 'user-hide', 'user')
 *     - Graph filters      → stash(ids, 'filter', 'filter') / restoreByReason
 *     - Follow the Money   → dim(nonPathIds) / undim(nonPathIds)
 *     - SSE discovery      → dim(newIds, 0.55) then undim when context arrives
 *     - Algorithms         → addToCanvas / dim / stash('algorithm', ...)
 *
 *   NO component should call `useGraphStore.setElements` or write `_visibility`
 *   directly after migration. Controller is the single entry point.
 *
 *   PHASE 0 BEHAVIOUR: the controller exists and wraps the existing store,
 *   but no other component routes through it yet. Phase 1+ will migrate
 *   the six visibility mechanisms one at a time.
 */

import type { CytoscapeElement } from '../types/graph';
import { useGraphStore } from '../stores/useGraphStore';
import { useStashStore, type StashReason, type StashInitiator, type StashedElement } from '../stores/useStashStore';
import { VISIBILITY } from '../constants/visibility';
import { beginControlledWrite, endControlledWrite } from './canvasControlledWrite';
import { toast } from 'sonner';

// Re-exported for back-compat / easy imports from the controller surface.
export { beginControlledWrite, endControlledWrite } from './canvasControlledWrite';

// Helper: wrap a store-mutating block so deprecation warnings don't fire.
function controlled<T>(fn: () => T): T {
    beginControlledWrite();
    try { return fn(); } finally { endControlledWrite(); }
}

// ── Large-graph soft warning ────────────────────────────────────────
// Toast once per session at each threshold to nudge the user to save.
// Without this, a 2000+ node investigation silently fills sessionStorage
// (which is per-tab and capped at ~5 MB), and the persist write
// eventually fails — historically presenting as a blank canvas with a
// "quota exceeded" error boundary.
const _LARGE_GRAPH_THRESHOLDS = [1500, 3000] as const;
const _largeGraphWarned = new Set<number>();
function maybeWarnLargeGraph(nodeCount: number): void {
    for (const threshold of _LARGE_GRAPH_THRESHOLDS) {
        if (nodeCount >= threshold && !_largeGraphWarned.has(threshold)) {
            _largeGraphWarned.add(threshold);
            toast.warning(
                `Graph is getting large (${nodeCount.toLocaleString()} nodes). ` +
                `Save Snapshot now to preserve this investigation across reloads.`,
                { duration: 8000 },
            );
        }
    }
}

// ── Helpers ──────────────────────────────────────────────────────────

function isNodeEl(el: CytoscapeElement): boolean {
    return !el.data.source;
}

function connectedEdgeIds(nodeId: string, elements: CytoscapeElement[]): string[] {
    const out: string[] = [];
    for (const el of elements) {
        if (el.data.source === nodeId || el.data.target === nodeId) {
            if (el.data.id) out.push(el.data.id);
        }
    }
    return out;
}

// ── The service ──────────────────────────────────────────────────────

export interface AddToCanvasOptions {
    /** Human-readable label for tracing / telemetry (e.g. "sse:backward_trace"). */
    source: string;
    /** Skip pushing an undo snapshot before the add. Default: false (push snapshot). */
    skipSnapshot?: boolean;
}

export const canvasController = {
    // ── Presence ─────────────────────────────────────────────────────

    /**
     * Add elements to the canvas. Deduplicates by data.id. Pushes an undo
     * snapshot first unless `skipSnapshot` is set.
     */
    addToCanvas(elements: CytoscapeElement[], opts: AddToCanvasOptions): void {
        if (!elements || elements.length === 0) return;
        const graph = useGraphStore.getState();
        controlled(() => {
            if (!opts.skipSnapshot) graph.pushSnapshot();
            graph.addElements(elements);
        });
        // After the add, count nodes and warn if we crossed a threshold.
        const nodeCount = useGraphStore.getState().elements
            .filter(el => !el.data.source).length;
        maybeWarnLargeGraph(nodeCount);
    },

    /**
     * Remove elements from the canvas and place them in the stash, tagged
     * with a reason. Orphan edges (connected to a stashed node) are auto-
     * stashed with reason='orphan'.
     *
     * Idempotent: already-stashed ids are silently skipped.
     */
    stash(ids: string[], reason: StashReason, by: StashInitiator): void {
        if (!ids || ids.length === 0) return;
        const graph = useGraphStore.getState();
        const stashStore = useStashStore.getState();

        const elements = graph.elements;
        const idSet = new Set(ids);
        const toStash: Array<{ id: string; entry: StashedElement }> = [];
        const removeIds: string[] = [];
        const now = Date.now();

        // Grab the live Cytoscape instance so we can capture each node's
        // current layout position before it's removed from the canvas.
        // Without this, a restored node arrives with no position and the
        // GraphCanvas expansion logic piles it on the centroid of its
        // neighbors, stacking many restored nodes on top of each other.
        const cy = (typeof window !== 'undefined' ? (window as any).__cy : null);

        for (const el of elements) {
            if (!el.data.id) continue;
            if (!idSet.has(el.data.id)) continue;
            if (stashStore.hasEntry(el.data.id)) continue;

            if (isNodeEl(el)) {
                const orphanEdgeIds = connectedEdgeIds(el.data.id, elements);
                // Snapshot the node's cy position onto a copy of the element
                // so restore() can drop it back where it was. Defensive try/catch
                // in case cy's position() returns something unexpected (stale
                // reference, pre-layout node, etc.) — never crash the stash op
                // because of a position capture failure.
                let elementToStore = el;
                if (cy) {
                    try {
                        const node = cy.getElementById(el.data.id);
                        if (node && node.length > 0) {
                            const p = node.position();
                            if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) {
                                elementToStore = { ...el, position: { x: p.x, y: p.y } };
                            }
                        }
                    } catch { /* fall back to el without position */ }
                }
                toStash.push({
                    id: el.data.id,
                    entry: {
                        element: elementToStore,
                        group: 'nodes',
                        reason,
                        stashedBy: by,
                        stashedAt: now,
                        connectedEdgeIds: orphanEdgeIds.length ? orphanEdgeIds : undefined,
                    },
                });
                removeIds.push(el.data.id);

                // Auto-stash orphan edges
                for (const edgeId of orphanEdgeIds) {
                    if (stashStore.hasEntry(edgeId)) continue;
                    const edgeEl = elements.find(e => e.data.id === edgeId);
                    if (!edgeEl) continue;
                    toStash.push({
                        id: edgeId,
                        entry: {
                            element: edgeEl,
                            group: 'edges',
                            reason: 'orphan',
                            stashedBy: by,
                            stashedAt: now,
                            orphanOf: el.data.id,
                        },
                    });
                    removeIds.push(edgeId);
                }
            } else {
                toStash.push({
                    id: el.data.id,
                    entry: {
                        element: el,
                        group: 'edges',
                        reason,
                        stashedBy: by,
                        stashedAt: now,
                    },
                });
                removeIds.push(el.data.id);
            }
        }

        if (toStash.length === 0) return;

        controlled(() => {
            if (by === 'user') graph.pushSnapshot();
            stashStore.setEntries(toStash);
            graph.removeElements(removeIds);
        });
    },

    /**
     * Restore specific stashed ids back to the canvas. If a restored node
     * had orphan edges, those edges are restored too (only if both endpoints
     * are on the canvas — either restored in the same call or already present).
     *
     * `by` defaults to 'user' (pushes an undo snapshot). Programmatic callers
     * (e.g. filter-relax on filter input change) should pass `'filter'` or
     * another non-user initiator so they don't pollute the undo stack.
     */
    restore(ids: string[], by: StashInitiator = 'user'): void {
        if (!ids || ids.length === 0) return;
        const graph = useGraphStore.getState();
        const stashStore = useStashStore.getState();

        const toAdd: CytoscapeElement[] = [];
        const toRemoveFromStash: string[] = [];
        const restoredNodeIds = new Set<string>();

        // First pass: collect requested entries
        for (const id of ids) {
            const entry = stashStore.entries[id];
            if (!entry) continue;
            toAdd.push(entry.element);
            toRemoveFromStash.push(id);
            if (entry.group === 'nodes') restoredNodeIds.add(id);
        }

        if (toAdd.length === 0) return;

        // Second pass: pick up orphan edges owned by restored nodes, if the
        // other endpoint is either on-canvas or also being restored in this call.
        const existingIds = new Set(graph.elements.map(e => e.data.id));
        for (const nodeId of restoredNodeIds) {
            for (const [edgeId, entry] of Object.entries(stashStore.entries)) {
                if (entry.reason !== 'orphan') continue;
                if (entry.orphanOf !== nodeId) continue;
                const edgeEl = entry.element;
                const src = edgeEl.data.source;
                const tgt = edgeEl.data.target;
                const srcOk = !!src && (existingIds.has(src) || restoredNodeIds.has(src));
                const tgtOk = !!tgt && (existingIds.has(tgt) || restoredNodeIds.has(tgt));
                if (srcOk && tgtOk) {
                    toAdd.push(edgeEl);
                    toRemoveFromStash.push(edgeId);
                }
            }
        }

        controlled(() => {
            if (by === 'user') graph.pushSnapshot();
            graph.addElements(toAdd);
            stashStore.removeEntries(toRemoveFromStash);
        });
    },

    /** Restore every stashed element whose reason matches. */
    restoreByReason(reason: StashReason, by: StashInitiator = 'user'): void {
        const entries = useStashStore.getState().getByReason(reason);
        if (entries.length === 0) return;
        const ids = entries
            .map(e => e.element.data.id)
            .filter((id): id is string => !!id);
        this.restore(ids, by);
    },

    /**
     * Permanently remove elements from canvas AND stash.
     * Use for "Clear Canvas" / "New Investigation" semantics.
     */
    remove(ids: string[]): void {
        if (!ids || ids.length === 0) return;
        const graph = useGraphStore.getState();
        const stashStore = useStashStore.getState();

        controlled(() => {
            graph.pushSnapshot();
            graph.removeElements(ids);
            stashStore.removeEntries(ids);
        });
    },

    // ── Opacity (transient hints only — never 0.0) ───────────────────

    /**
     * Dim elements on the canvas. `level` must be in (0, 1); defaults to
     * DIMMED (0.55). Writing 0 is refused — use `stash` to hide.
     *
     * `reason` is a free-form audit tag (e.g. "follow_money", "sse_pruned",
     * "filter_dim") logged via console.debug. Use it to make the v17+ Railway
     * + canvasDebug pipeline tell you WHICH pass dimmed any given node — the
     * v16 root S6 dim couldn't be traced back without one.
     */
    dim(ids: string[], level: number = VISIBILITY.DIMMED, reason: string = 'unspecified'): void {
        if (!ids || ids.length === 0) return;
        if (!(level > 0 && level <= 1)) {
            console.error('[canvasController] dim() level must be in (0, 1]. Use stash() to hide.');
            return;
        }
        const graph = useGraphStore.getState();
        const updates = new Map<string, number>();
        for (const id of ids) updates.set(id, level);
        controlled(() => graph.setVisibilityBulk(updates));
        console.debug('[canvasController] visibility.dim', {
            count: ids.length, level, reason,
            sampleIds: ids.slice(0, 3).map(id => id.slice(0, 18)),
        });
    },

    /** Reset opacity to VISIBLE (1.0) for the given ids. */
    undim(ids: string[], reason: string = 'unspecified'): void {
        if (!ids || ids.length === 0) return;
        const graph = useGraphStore.getState();
        const updates = new Map<string, number>();
        for (const id of ids) updates.set(id, VISIBILITY.VISIBLE);
        controlled(() => graph.setVisibilityBulk(updates));
        console.debug('[canvasController] visibility.undim', {
            count: ids.length, reason,
            sampleIds: ids.slice(0, 3).map(id => id.slice(0, 18)),
        });
    },

    /** Reset opacity of every element on canvas to VISIBLE. */
    clearAllDims(reason: string = 'unspecified'): void {
        const graph = useGraphStore.getState();
        const updates = new Map<string, number>();
        for (const el of graph.elements) {
            if (el.data.id && el.data._visibility !== VISIBILITY.VISIBLE) {
                updates.set(el.data.id, VISIBILITY.VISIBLE);
            }
        }
        if (updates.size > 0) controlled(() => graph.setVisibilityBulk(updates));
        console.debug('[canvasController] visibility.clearAllDims', {
            cleared: updates.size, reason,
        });
    },

    // ── Queries ──────────────────────────────────────────────────────

    isOnCanvas(id: string): boolean {
        return useGraphStore.getState().elements.some(el => el.data.id === id);
    },

    isStashed(id: string): boolean {
        return useStashStore.getState().hasEntry(id);
    },

    whyStashed(id: string): StashReason | null {
        const e = useStashStore.getState().entries[id];
        return e ? e.reason : null;
    },

    getStashed(filterBy?: StashReason): StashedElement[] {
        const s = useStashStore.getState();
        return filterBy ? s.getByReason(filterBy) : s.getAll();
    },
};

/**
 * React hook variant for components that want to subscribe to stash
 * / graph changes. Re-renders on any relevant state change.
 */
export function useCanvasController() {
    // Touch both stores so components subscribing via this hook re-render
    // when either changes.
    useGraphStore(s => s.elements);
    useStashStore(s => s.entries);
    return canvasController;
}
